import React, { useState, useEffect, useMemo } from "react";
import {
  getUsers,
  markAttendance,
  getAttendance,
} from "../services/api";
import UserProfileDrawer from "../screens/UserProfileDrawer";
import axios from "axios";
import { debounce } from "lodash";

export default function Usercard() {
  const [userList, setUserList] = useState([]);
  const [visibleUsers, setVisibleUsers] = useState([]);
  const today = new Date().toISOString().split("T")[0];
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [attendanceMap, setAttendanceMap] = useState({});
  const [typeFilter, setTypeFilter] = useState("all"); // "all", "Sevak", "Guest"
  const [statusFilter, setStatusFilter] = useState("active"); // "all", "active", "inactive"
  const [expectedMudatMap, setExpectedMudatMap] = useState({});
  const [viewType, setViewType] = useState("card"); // "card" or "list"

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const handler = debounce(() => setDebouncedSearchTerm(searchTerm), 400);
    handler();
    return () => handler.cancel();
  }, [searchTerm]);

  const fetchUsers = async () => {
    try {
      const res = await getUsers();
      setUserList(res.data);
      setVisibleUsers(res.data.slice(0, 30));
      await Promise.all(res.data.slice(0, 30).map((user) => loadAttendance(user.id)));
      
      // Fetch expected mudat for all users
      const userIds = res.data.map(u => u.id);
      fetchExpectedMudat(userIds);
    } catch (err) {
      console.error("Failed to fetch users:", err);
    }
  };

  const fetchExpectedMudat = async (userIds) => {
    try {
      const res = await axios.post("https://api.rangpursadhima.cloud/api/mudat/user-mudat/bulk", { userIds });
      setExpectedMudatMap(res.data);
    } catch (err) {
      console.error("Failed to fetch expected mudat:", err);
    }
  };

  const loadAttendance = async (userId) => {
    try {
      const res = await getAttendance(userId);
      setAttendanceMap((prev) => ({ ...prev, [userId]: res.data }));
    } catch (err) {
      console.error("Failed to fetch attendance:", err);
    }
  };

  const handleAttendance = async (id, slot) => {
    const slotMap = {
      morning: "Morning_presentation",
      evening: "evening_presentation",
    };
    try {
      await markAttendance(id, slotMap[slot]);
      await loadAttendance(id);
      fetchSummary();
      
      // Refresh user_mudat for this user
      try {
        const mudatRes = await axios.get(`https://api.rangpursadhima.cloud/api/mudat/user-mudat/${id}`);
        setExpectedMudatMap((prev) => ({
          ...prev,
          [id]: mudatRes.data
        }));
      } catch (mudatErr) {
        // No mudat data yet
      }
    } catch (err) {
      console.error("Attendance failed", err);
    }
  };

  const fetchSummary = async () => {
    try {
      await axios.get(`https://api.rangpursadhima.cloud/api/attendance/summary?date=${today}`);
    } catch (err) {
      console.error("Failed to fetch summary", err);
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    const confirmMsg = newStatus === 'inactive' 
      ? "Are you sure you want to deactivate this user?" 
      : "Are you sure you want to activate this user?";
    
    const confirm = window.confirm(confirmMsg);
    if (!confirm) return;
    
    try {
      await axios.put(`https://api.rangpursadhima.cloud/api/users/${id}/status`, { status: newStatus });
      setUserList((prev) => 
        prev.map((u) => u.id === id ? { ...u, status: newStatus } : u)
      );
      setVisibleUsers((prev) => 
        prev.map((u) => u.id === id ? { ...u, status: newStatus } : u)
      );
    } catch (err) {
      console.error("Failed to update user status", err);
      alert("Failed to update status");
    }
  };

  const filteredUsers = useMemo(() => {
    // Use userList when any filter is active (search, type filter, or status filter)
    // Only use visibleUsers when showing default "all" view
    const hasActiveFilter = debouncedSearchTerm || typeFilter !== "all" || statusFilter !== "active";
    let baseList = hasActiveFilter ? userList : visibleUsers;
    
    // Apply status filter
    if (statusFilter !== "all") {
      baseList = baseList.filter(user => (user.status || 'active') === statusFilter);
    }
    
    // Apply tag filter
    if (typeFilter !== "all") {
      baseList = baseList.filter(user => (user.tag || 'Sevak') === typeFilter);
    }

    if (!debouncedSearchTerm) {
      // No search - return filtered users sorted by Sevakcode
      return [...baseList].sort((a, b) => {
        return a.Sevakcode.toString().localeCompare(b.Sevakcode.toString(), undefined, {
          numeric: true,
          sensitivity: 'base'
        });
      });
    }

    const searchTermLower = debouncedSearchTerm.toLowerCase().trim();
    
    // Check if search term looks like a Sevakcode (starts with s, s-, or is SHORT numeric like 1-4 digits)
    // Mobile numbers are 10 digits, so anything >= 5 digits should NOT be treated as Sevakcode
    const isShortNumeric = /^\d{1,4}$/.test(searchTermLower);
    const isSevakCodeSearch = /^s[-]?\d*$/i.test(searchTermLower) || isShortNumeric;
    
    // Check if it looks like a mobile number (5+ digits)
    const isMobileSearch = /^\d{5,}$/.test(searchTermLower);
    
    let filtered = baseList.filter((user) => {
      const mobile = (user.MOBILE_NO || '').toString();
      
      // Always check mobile number for numeric searches with 5+ digits
      if (isMobileSearch) {
        return mobile.includes(searchTermLower);
      }
      
      // For Sevakcode search - do exact/smart matching
      if (isSevakCodeSearch) {
        const sevakCode = (user.Sevakcode || '').toLowerCase();
        
        // Normalize both: remove 's-' or 's' prefix for comparison
        const normalizedSearch = searchTermLower.replace(/^s[-]?/, '');
        const normalizedCode = sevakCode.replace(/^s[-]?/, '');
        
        // If search is just "s" or "s-", show all
        if (normalizedSearch === '') {
          return true;
        }
        
        // Exact number match - "1" matches "1", "11" matches "11"
        if (normalizedCode === normalizedSearch) {
          return true;
        }
        
        // Also check mobile for short numbers (1-4 digits)
        if (isShortNumeric && mobile.includes(searchTermLower)) {
          return true;
        }
        
        return false;
      }
      
      // For other searches (name, city) - use includes
      const firstName = (user.FIRST_NAME || '').toLowerCase();
      const lastName = (user.LAST_NAME || '').toLowerCase();
      const city = (user.CITY_AREA_VILLAGE || '').toLowerCase();
      
      return firstName.includes(searchTermLower) ||
             lastName.includes(searchTermLower) ||
             city.includes(searchTermLower) ||
             mobile.includes(searchTermLower) ||
             `${firstName} ${lastName}`.includes(searchTermLower);
    });

    // Sort results by Sevakcode
    filtered.sort((a, b) => {
      return a.Sevakcode.toString().localeCompare(b.Sevakcode.toString(), undefined, {
        numeric: true,
        sensitivity: 'base'
      });
    });

    return filtered;
  }, [debouncedSearchTerm, userList, visibleUsers, typeFilter, statusFilter]);

  // ✅ Debounced & batched attendance loading for filtered users
  useEffect(() => {
    const usersToLoad = filteredUsers.filter((user) => !attendanceMap[user.id]);

    if (usersToLoad.length === 0) return;

    const timeoutId = setTimeout(() => {
      usersToLoad.slice(0, 15).forEach((user) => {
        loadAttendance(user.id);
      });
    }, 300); // Adjust delay as needed

    return () => clearTimeout(timeoutId);
  }, [filteredUsers]);

  return (
    <div className="bg-gray-50 min-h-screen p-4 sm:p-6">
      <div className="max-w-7xl mx-auto w-full">
        <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4 mb-4 w-full">
          <input
            type="text"
            placeholder="Search by name, city, mobile or code (S-1, G-1)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-4 py-2 border rounded-md w-full sm:w-2/3"
          />
          <button
            onClick={() => setSelectedUser({})}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 w-full sm:w-auto"
          >
            + Add New
          </button>
        </div>

        {/* Tag Filter Buttons */}
        <div className="flex flex-wrap gap-2 mb-4 items-center">
          <button
            onClick={() => setTypeFilter("all")}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${
              typeFilter === "all"
                ? "bg-gray-800 text-white"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
          >
            All ({userList.filter(u => statusFilter === 'all' || (u.status || 'active') === statusFilter).length})
          </button>
          <button
            onClick={() => setTypeFilter("Sevak")}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${
              typeFilter === "Sevak"
                ? "bg-purple-600 text-white"
                : "bg-purple-100 text-purple-700 hover:bg-purple-200"
            }`}
          >
            Sevak ({userList.filter(u => (u.tag || 'Sevak') === 'Sevak' && (statusFilter === 'all' || (u.status || 'active') === statusFilter)).length})
          </button>
          <button
            onClick={() => setTypeFilter("Guest")}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${
              typeFilter === "Guest"
                ? "bg-orange-600 text-white"
                : "bg-orange-100 text-orange-700 hover:bg-orange-200"
            }`}
          >
            Guest ({userList.filter(u => u.tag === 'Guest' && (statusFilter === 'all' || (u.status || 'active') === statusFilter)).length})
          </button>

          {/* View Toggle */}
          <div className="flex gap-1 bg-gray-200 p-1 rounded-lg sm:ml-auto">
            <button
              onClick={() => setViewType("card")}
              className={`px-3 py-1.5 rounded-md font-medium transition-colors flex items-center gap-1 ${
                viewType === "card"
                  ? "bg-white text-gray-800 shadow-sm"
                  : "text-gray-600 hover:text-gray-800"
              }`}
              title="Card View"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
              <span className="hidden sm:inline">Card</span>
            </button>
            <button
              onClick={() => setViewType("list")}
              className={`px-3 py-1.5 rounded-md font-medium transition-colors flex items-center gap-1 ${
                viewType === "list"
                  ? "bg-white text-gray-800 shadow-sm"
                  : "text-gray-600 hover:text-gray-800"
              }`}
              title="List View"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
              <span className="hidden sm:inline">List</span>
            </button>
          </div>
        </div>

        {/* Status Filter */}
        <div className="flex flex-wrap gap-2 mb-6 items-center">
          <span className="text-sm text-gray-500 font-medium">Status:</span>
          <button
            onClick={() => setStatusFilter("active")}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              statusFilter === "active"
                ? "bg-green-600 text-white"
                : "bg-green-100 text-green-700 hover:bg-green-200"
            }`}
          >
            Active ({userList.filter(u => 
              (u.status || 'active') === 'active' && 
              (typeFilter === 'all' || (u.tag || 'Sevak') === typeFilter)
            ).length})
          </button>
          <button
            onClick={() => setStatusFilter("inactive")}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              statusFilter === "inactive"
                ? "bg-red-600 text-white"
                : "bg-red-100 text-red-700 hover:bg-red-200"
            }`}
          >
            Inactive ({userList.filter(u => 
              u.status === 'inactive' && 
              (typeFilter === 'all' || (u.tag || 'Sevak') === typeFilter)
            ).length})
          </button>
          <button
            onClick={() => setStatusFilter("all")}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              statusFilter === "all"
                ? "bg-gray-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            All ({userList.filter(u => 
              typeFilter === 'all' || (u.tag || 'Sevak') === typeFilter
            ).length})
          </button>
        </div>

        <div className="overflow-auto w-full">
          {filteredUsers.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              No users to display.
            </div>
          ) : viewType === "card" ? (
            /* Card View */
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
              {filteredUsers.map((user) => {
                const attendance = attendanceMap[user.id] || {};
                const isInactive = user.status === 'inactive';
                return (
                  <div
                    key={user.id}
                    className={`bg-white rounded-xl shadow-md p-6 flex flex-col justify-between relative w-full ${
                      isInactive ? 'opacity-60 border-2 border-red-200' : ''
                    }`}
                  >
                    {isInactive && (
                      <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                        Inactive
                      </div>
                    )}
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h2 className="text-base font-bold mb-1">
                          {user.FIRST_NAME} {user.LAST_NAME}
                        </h2>
                        <p className="text-blue-600 font-bold text-sm">
                          {user.CITY_AREA_VILLAGE}
                        </p>
                      </div>
                      <div className="w-[48px] h-[48px] rounded-full overflow-hidden">
                        <img
                          src={
                            user.image_url && user.image_url !== "null"
                              ? user.image_url.startsWith("/upload")
                                ? `https://api.rangpursadhima.cloud${user.image_url}`
                                : user.image_url
                              : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                  user.FIRST_NAME?.charAt(0) || "S"
                                )}&background=0D8ABC&color=fff`
                          }
                          alt="Profile"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>

                    <div className="flex flex-wrap justify-between items-center text-sm text-gray-700 my-3 border-b border-red-800 pb-2 gap-y-2">
                      <span className={`text-xs font-semibold px-3 py-1 rounded-full ${
                        user.tag === 'Guest' 
                          ? 'bg-orange-100 text-orange-800' 
                          : 'bg-purple-100 text-purple-800'
                      }`}>
                        {user.tag === 'Guest' ? 'GUEST' : 'SEVAK'}
                      </span>
                      <p className="text-sm font-semibold">📞 {user.MOBILE_NO || "N/A"}</p>
                      <button
                        onClick={() => handleToggleStatus(user.id, user.status || 'active')}
                        className={`px-3 py-1 rounded text-xs font-medium ${
                          (user.status || 'active') === 'active'
                            ? 'text-green-700 bg-green-100 hover:bg-green-200'
                            : 'text-red-700 bg-red-100 hover:bg-red-200'
                        }`}
                      >
                        {(user.status || 'active') === 'active' ? '✓ Active' : '✗ Inactive'}
                      </button>
                    </div>

                    <div className="grid grid-cols-2 text-sm mt-3 border-2 border-gray-300 rounded-md overflow-hidden">
                      <div className="text-center border-r py-2">
                        <div className="text-gray-500 font-medium">Sevakcode</div>
                        <div className="text-gray-700 font-semibold">{user.Sevakcode}</div>
                      </div>
                      <div className="text-center bg-gray-300 flex items-center justify-center w-full py-2">
                        <button
                          onClick={() => {
                            loadAttendance(user.id);
                            setSelectedUser(user);
                          }}
                          className="text-gray-700 font-semibold hover:text-red-900 w-full"
                        >
                          View
                        </button>
                      </div>
                    </div>

                    {/* Last Mudat */}
                    {attendance?.lastMarked && (
                      <div className="text-center text-xs text-green-700 font-semibold mt-2">
                        Last Mudat: {new Date(attendance.lastMarked).toLocaleDateString()}
                      </div>
                    )}

                    {/* Expected Next Mudat - ONLY if user_mudat exists for this user */}
                    {expectedMudatMap[user.id]?.next_mudat_date && (() => {
                      const nextDate = new Date(expectedMudatMap[user.id].next_mudat_date);
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const isLate = today > nextDate;
                      const diffDays = isLate ? Math.floor((today - nextDate) / (1000 * 60 * 60 * 24)) : 0;
                      
                      return (
                        <div className={`mt-2 p-2 rounded border ${isLate ? 'bg-red-50 border-red-300' : 'bg-orange-50 border-orange-200'}`}>
                          <div className="flex justify-between items-center text-xs">
                            <span className={`font-semibold ${isLate ? 'text-red-800' : 'text-orange-800'}`}>
                              {isLate ? 'Late Mudat:' : 'Next Mudat:'}
                            </span>
                            <span className={`font-bold ${isLate ? 'text-red-700' : 'text-orange-700'}`}>
                              {nextDate.toLocaleDateString('en-IN', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric'
                              })}
                            </span>
                          </div>
                          <div className={`text-xs text-right ${isLate ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                            {isLate ? `Late by ${diffDays} days` : `(${expectedMudatMap[user.id].mudat_days} days)`}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Insurance Status */}
                    <div className="flex justify-center gap-4 mt-2 text-xs font-semibold">
                      <span>
                        Medical:{" "}
                        {user.medical_insurance === "yes"
                          ? <span className="text-green-600">Y</span>
                          : <span className="text-red-500">N</span>}
                      </span>
                      <span>
                        Accident:{" "}
                        {user.accident_insurance === "yes"
                          ? <span className="text-green-600">Y</span>
                          : <span className="text-red-500">N</span>}
                      </span>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 mt-4">
                      <button
                        onClick={() => handleAttendance(user.id, "morning")}
                        disabled={
                          !!attendance?.lastMorningMarked &&
                          attendance.lastMorningMarked.startsWith(today)
                        }
                        className={`w-full py-2 rounded text-white font-medium ${
                          attendance?.lastMorningMarked?.startsWith(today)
                            ? "bg-blue-200 cursor-not-allowed"
                            : "bg-blue-600 hover:bg-blue-700"
                        }`}
                      >
                        Morning Present
                      </button>

                      <button
                        onClick={() => handleAttendance(user.id, "evening")}
                        disabled={
                          !!attendance?.lastEveningMarked &&
                          attendance.lastEveningMarked.startsWith(today)
                        }
                        className={`w-full py-2 rounded text-white font-medium ${
                          attendance?.lastEveningMarked?.startsWith(today)
                            ? "bg-green-200 cursor-not-allowed"
                            : "bg-green-600 hover:bg-green-700"
                        }`}
                      >
                        Evening Present
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* List View */
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-red-600 text-white">
                  <tr>
                    <th className="p-3 text-left">Photo</th>
                    <th className="p-3 text-left">Code</th>
                    <th className="p-3 text-left">Name</th>
                    <th className="p-3 text-left">City</th>
                    <th className="p-3 text-left">Mobile</th>
                    <th className="p-3 text-center">Type</th>
                    <th className="p-3 text-center">Status</th>
                    <th className="p-3 text-center">Last Mudat</th>
                    <th className="p-3 text-center">Morning</th>
                    <th className="p-3 text-center">Evening</th>
                    <th className="p-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user, index) => {
                    const attendance = attendanceMap[user.id] || {};
                    const isInactive = user.status === 'inactive';
                    return (
                      <tr key={user.id} className={`border-b hover:bg-gray-50 ${
                        isInactive 
                          ? 'bg-red-50 opacity-70' 
                          : index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                      }`}>
                        <td className="p-3">
                          <div className="w-10 h-10 rounded-full overflow-hidden">
                            <img
                              src={
                                user.image_url && user.image_url !== "null"
                                  ? user.image_url.startsWith("/upload")
                                    ? `https://api.rangpursadhima.cloud${user.image_url}`
                                    : user.image_url
                                  : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                      user.FIRST_NAME?.charAt(0) || "S"
                                    )}&background=0D8ABC&color=fff`
                              }
                              alt="Profile"
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </td>
                        <td className="p-3 font-semibold text-gray-700">{user.Sevakcode}</td>
                        <td className="p-3">
                          <div className="font-medium text-gray-900">{user.FIRST_NAME} {user.LAST_NAME}</div>
                        </td>
                        <td className="p-3 text-blue-600 font-medium">{user.CITY_AREA_VILLAGE}</td>
                        <td className="p-3 text-gray-600">{user.MOBILE_NO || "N/A"}</td>
                        <td className="p-3 text-center">
                          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                            user.tag === 'Guest' 
                              ? 'bg-orange-100 text-orange-800' 
                              : 'bg-purple-100 text-purple-800'
                          }`}>
                            {user.tag === 'Guest' ? 'GUEST' : 'SEVAK'}
                          </span>
                        </td>
                        <td className="p-3 text-center">
                          <button
                            onClick={() => handleToggleStatus(user.id, user.status || 'active')}
                            className={`text-xs font-semibold px-2 py-1 rounded-full ${
                              (user.status || 'active') === 'active'
                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                : 'bg-red-100 text-red-700 hover:bg-red-200'
                            }`}
                          >
                            {(user.status || 'active') === 'active' ? '✓ Active' : '✗ Inactive'}
                          </button>
                        </td>
                        <td className="p-3 text-center text-xs">
                          {attendance?.lastMarked ? (
                            <span className="text-green-700 font-medium">
                              {new Date(attendance.lastMarked).toLocaleDateString()}
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          <button
                            onClick={() => handleAttendance(user.id, "morning")}
                            disabled={
                              !!attendance?.lastMorningMarked &&
                              attendance.lastMorningMarked.startsWith(today)
                            }
                            className={`px-3 py-1 rounded text-xs font-medium ${
                              attendance?.lastMorningMarked?.startsWith(today)
                                ? "bg-blue-100 text-blue-400 cursor-not-allowed"
                                : "bg-blue-600 text-white hover:bg-blue-700"
                            }`}
                          >
                            {attendance?.lastMorningMarked?.startsWith(today) ? "✓ Done" : "Mark"}
                          </button>
                        </td>
                        <td className="p-3 text-center">
                          <button
                            onClick={() => handleAttendance(user.id, "evening")}
                            disabled={
                              !!attendance?.lastEveningMarked &&
                              attendance.lastEveningMarked.startsWith(today)
                            }
                            className={`px-3 py-1 rounded text-xs font-medium ${
                              attendance?.lastEveningMarked?.startsWith(today)
                                ? "bg-green-100 text-green-400 cursor-not-allowed"
                                : "bg-green-600 text-white hover:bg-green-700"
                            }`}
                          >
                            {attendance?.lastEveningMarked?.startsWith(today) ? "✓ Done" : "Mark"}
                          </button>
                        </td>
                        <td className="p-3 text-center">
                          <button
                            onClick={() => {
                              loadAttendance(user.id);
                              setSelectedUser(user);
                            }}
                            className="text-blue-600 hover:text-blue-800 font-medium text-xs bg-blue-50 px-3 py-1 rounded hover:bg-blue-100"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {searchTerm === "" && typeFilter === "all" && statusFilter === "active" && visibleUsers.length < userList.length && (
          <div className="mt-6 text-center">
            <button
              onClick={async () => {
                const moreUsers = userList.slice(
                  visibleUsers.length,
                  visibleUsers.length + 30
                );
                await Promise.all(moreUsers.map((u) => loadAttendance(u.id)));
                setVisibleUsers((prev) => [...prev, ...moreUsers]);
              }}
              className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
            >
              Load More
            </button>
          </div>
        )}
      </div>

      {selectedUser !== null && (
        <UserProfileDrawer
          selectedUser={selectedUser}
          onClose={() => setSelectedUser(null)}
          onSave={async (savedData) => {
            const id = savedData?.id || selectedUser?.id;

            // Refresh the full user list so search and cards show updated data
            await fetchUsers();

            if (id) {
              try {
                // Refresh attendance
                const res = await getAttendance(id);
                setAttendanceMap((prev) => ({
                  ...prev,
                  [id]: res.data
                }));
                
                // Refresh user_mudat for this user
                try {
                  const mudatRes = await axios.get(`https://api.rangpursadhima.cloud/api/mudat/user-mudat/${id}`);
                  setExpectedMudatMap((prev) => ({
                    ...prev,
                    [id]: mudatRes.data
                  }));
                } catch (mudatErr) {
                  // No mudat data - remove from map
                  setExpectedMudatMap((prev) => {
                    const newMap = { ...prev };
                    delete newMap[id];
                    return newMap;
                  });
                }
              } catch (err) {
                console.error("Refresh failed:", err);
              }
            }
          }}
        />
      )}
    </div>
  );
}